import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronLeft, Plus, Edit, Trash2, BookOpen, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Check } from 'lucide-react';
import { Lesson, Category, LessonFormat } from '@/types/ai-devquest';
import { aiDevQuestService } from '@/services/aiDevQuestService';
import { Timestamp } from 'firebase/firestore';

interface AIDevQuestLessonManagementSectionProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

/**
 * Admin section for managing AI DevQuest lessons
 */
const AIDevQuestLessonManagementSection: React.FC<AIDevQuestLessonManagementSectionProps> = ({
  isExpanded,
  setIsExpanded
}) => {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Form state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] = useState(false);
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  
  // Form fields
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [lessonCategory, setLessonCategory] = useState('');
  const [lessonIcon, setLessonIcon] = useState('');
  const [lessonEstimatedTime, setLessonEstimatedTime] = useState('');
  const [lessonFormats, setLessonFormats] = useState<LessonFormat[]>([]);
  const [lessonVideoUrl, setLessonVideoUrl] = useState('');
  const [lessonDocumentUrl, setLessonDocumentUrl] = useState('');
  const [lessonExternalLinks, setLessonExternalLinks] = useState('');
  const [lessonNotes, setLessonNotes] = useState('');
  
  // Category form fields
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  
  // Load data
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
 
      
      // Initialize default categories if needed
      await aiDevQuestService.initializeDefaultCategories();
      
      // Load categories and lessons
      const [categoriesData, lessonsData] = await Promise.all([
        aiDevQuestService.getCategories(),
        aiDevQuestService.getLessons()
      ]);
      
      console.log('Loaded categories:', categoriesData);
      setCategories(categoriesData);
      
      setLessons(lessonsData);
    } catch (err) {
      console.error('Error loading AI DevQuest data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initialize default categories when component mounts
  useEffect(() => {
    const initializeCategories = async () => {
      try {
        await aiDevQuestService.initializeDefaultCategories();
      } catch (err) {
        console.error('Error initializing default categories:', err);
      }
    };
    
    initializeCategories();
  }, []);
  
  useEffect(() => {
    if (isExpanded) {
      loadData();
    }
  }, [isExpanded]);
  
  // Reset form fields
  const resetForm = () => {
    setLessonTitle('');
    setLessonDescription('');
    setLessonCategory('');
    setLessonIcon('');
    setLessonEstimatedTime('');
    setLessonFormats([]);
    setLessonVideoUrl('');
    setLessonDocumentUrl('');
    setLessonExternalLinks('');
    setLessonNotes('');
  };
  
  // Reset category form fields
  const resetCategoryForm = () => {
    setCategoryName('');
    setCategoryDescription('');
  };
  
  // Handle format toggle
  const toggleFormat = (format: LessonFormat) => {
    if (lessonFormats.includes(format)) {
      setLessonFormats(lessonFormats.filter(f => f !== format));
    } else {
      setLessonFormats([...lessonFormats, format]);
    }
  };
  
  // Handle add lesson
  const handleAddLesson = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate form
      if (!lessonTitle.trim()) {
        setError('Lesson title is required');
        return;
      }
      
      if (!lessonCategory) {
        setError('Please select a category');
        return;
      }
      
      if (!lessonEstimatedTime || isNaN(parseInt(lessonEstimatedTime))) {
        setError('Please enter a valid estimated time in minutes');
        return;
      }
      
      // Create content object without empty fields
      const content: any = {};
      
      if (lessonVideoUrl.trim()) {
        content.videoUrl = lessonVideoUrl.trim();
      }
      
      if (lessonDocumentUrl.trim()) {
        content.documentUrl = lessonDocumentUrl.trim();
      }
       
     if (lessonExternalLinks.trim()) {
        content.externalLinks = lessonExternalLinks.split('\n').map(link => link.trim()).filter(Boolean)
;
      }
      
      if (lessonNotes.trim()) {
        content.notes = lessonNotes.trim();
      }
      
      // Create new lesson
      const newLesson = {
        title: lessonTitle.trim(),
        description: lessonDescription.trim(),
        category: lessonCategory,
        icon: lessonIcon.trim() || 'default',
        estimatedTime: parseInt(lessonEstimatedTime),
        formats: lessonFormats,
        content
      };
      
      // Add lesson to Firebase
      await aiDevQuestService.addLesson(newLesson);
      
      // Reset form and close dialog
      resetForm();
      setIsAddDialogOpen(false);
      
      // Show success message
      setSuccess('Lesson added successfully');
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error adding lesson:', err);
      setError('Failed to add lesson. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle edit lesson
  const handleEditLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate form
      if (!lessonTitle.trim()) {
        setError('Lesson title is required');
        return;
      }
      
      if (!lessonCategory) {
        setError('Please select a category');
        return;
      }
      
      if (!lessonEstimatedTime || isNaN(parseInt(lessonEstimatedTime))) {
        setError('Please enter a valid estimated time in minutes');
        return;
      }
      
      // Create content object without empty fields
      const content: any = {};
      
      if (lessonVideoUrl.trim()) {
        content.videoUrl = lessonVideoUrl.trim();
      }
      
      if (lessonDocumentUrl.trim()) {
        content.documentUrl = lessonDocumentUrl.trim();
      }
       
     if (lessonExternalLinks.trim()) {
        content.externalLinks = lessonExternalLinks.split('\n').map(link => link.trim()).filter(Boolean)
;
      }
      
      if (lessonNotes.trim()) {
        content.notes = lessonNotes.trim();
      }
      
      // Create updated lesson
      const updatedLesson = {
        title: lessonTitle.trim(),
        description: lessonDescription.trim(),
        category: lessonCategory,
        icon: lessonIcon.trim() || 'default',
        estimatedTime: parseInt(lessonEstimatedTime),
        formats: lessonFormats,
        content
      };
      
      // Update lesson in Firebase
      await aiDevQuestService.updateLesson(selectedLesson.id, updatedLesson);
      
      // Reset form and close dialog
      resetForm();
      setIsEditDialogOpen(false);
      setSelectedLesson(null);
      
      // Show success message
      setSuccess('Lesson updated successfully');
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error updating lesson:', err);
      setError('Failed to update lesson. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete lesson
  const handleDeleteLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Delete lesson from Firebase
      await aiDevQuestService.deleteLesson(selectedLesson.id);
      
      // Reset form and close dialog
      setIsDeleteDialogOpen(false);
      setSelectedLesson(null);
      
      // Show success message
      setSuccess('Lesson deleted successfully');
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error deleting lesson:', err);
      setError('Failed to delete lesson. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete category
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Delete category from Firebase
      await aiDevQuestService.deleteCategory(selectedCategory.id);
      
      // Reset form and close dialog
      setIsDeleteCategoryDialogOpen(false);
      setSelectedCategory(null);
      
      // Show success message
      setSuccess('Category deleted successfully');
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle add category
  const handleAddCategory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate form
      if (!categoryName.trim()) {
        setError('Category name is required');
        return;
      }
      
      // Create new category
      const newCategory = {
        name: categoryName.trim(),
        description: categoryDescription.trim()
      };
      
      // Add category to Firebase
      await aiDevQuestService.addCategory(newCategory);
      
      // Reset form and close dialog
      resetCategoryForm();
      setIsAddCategoryDialogOpen(false);
      
      // Show success message
      setSuccess('Category added successfully');
      
      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error adding category:', err);
      setError('Failed to add category. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save category orders to Firebase with debounce
  const saveCategoryOrders = async (updatedCategories: Category[]) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set saving state
    setIsSaving(true);
    
    // Set a new timeout to save after a delay
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await aiDevQuestService.updateCategoryOrders(
          updatedCategories.map((category, idx) => ({
            id: category.id,
            order: idx
          }))
        );
        
        setTimeout(() => setSuccess(null), 2000); // Auto-clear success message after 2 seconds
      } catch (err) {
        console.error('Error updating category orders:', err);
        setError('Failed to update category order. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }, 1000); // 1 second delay before saving to Firebase
  };
  
  // Move category up
  const moveCategoryUp = (index: number) => {
    if (index <= 0) return; // Already at the top
    
    // Reorder the categories
    const reorderedCategories = [...categories];
    const temp = reorderedCategories[index];
    reorderedCategories[index] = reorderedCategories[index - 1];
    reorderedCategories[index - 1] = temp;
    
    // Update the order field for each category
    const updatedCategories = reorderedCategories.map((category, idx) => ({
      ...category,
      order: idx
    }));
    
    // Update state immediately
    setCategories(updatedCategories);
    
    // Schedule save to Firebase with debounce
    saveCategoryOrders(updatedCategories);
  };
  
  // Move category down
  const moveCategoryDown = (index: number) => {
    if (index >= categories.length - 1) return; // Already at the bottom
    
    // Reorder the categories
    const reorderedCategories = [...categories];
    const temp = reorderedCategories[index];
    reorderedCategories[index] = reorderedCategories[index + 1];
    reorderedCategories[index + 1] = temp;
    
    // Update the order field for each category
    const updatedCategories = reorderedCategories.map((category, idx) => ({
      ...category,
      order: idx
    }));
    
    // Update state immediately
    setCategories(updatedCategories);
    
    // Schedule save to Firebase with debounce
    saveCategoryOrders(updatedCategories);
  };
  
  // Sort categories by order
  const sortedCategories = [...categories].sort((a, b) => {
    return (a.order ?? 0) - (b.order ?? 0);
  });
  
  // Open edit dialog with lesson data
  const openEditDialog = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setLessonTitle(lesson.title);
    setLessonDescription(lesson.description);
    setLessonCategory(lesson.category);
    setLessonIcon(lesson.icon);
    setLessonEstimatedTime(lesson.estimatedTime.toString());
    setLessonFormats(lesson.formats);
    setLessonVideoUrl(lesson.content.videoUrl || '');
    setLessonDocumentUrl(lesson.content.documentUrl || '');
    setLessonExternalLinks(lesson.content.externalLinks ? lesson.content.externalLinks.join('\n') : '');
    setLessonNotes(lesson.content.notes || '');
    setIsEditDialogOpen(true);
  };
  
  // Open delete dialog
  const openDeleteDialog = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setIsDeleteDialogOpen(true);
  };
  
  // Open delete category dialog
  const openDeleteCategoryDialog = (category: Category) => {
    setSelectedCategory(category);
    setIsDeleteCategoryDialogOpen(true);
  };
  
  return (
    <div className="p-6 bg-gray-100 rounded-lg shadow-sm dark:bg-gray-800 ai-devquest-section">
      <div className="flex items-center gap-2 mb-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <BookOpen className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        <h2 className="text-xl font-semibold dark:text-white">AI DevQuest Lessons</h2>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 ml-auto dark:text-gray-400" />
        ) : (
          <ChevronLeft className="h-5 w-5 ml-auto dark:text-gray-400" />
        )}
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-6">
          {/* Success/Error Messages */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert variant="default" className="mb-4 bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-800">
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-600 dark:text-green-400">{success}</AlertDescription>
            </Alert>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button 
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Lesson
            </Button>
            
            <Button 
              onClick={() => {
                resetCategoryForm();
                setIsAddCategoryDialogOpen(true);
              }}
              className="bg-transparent border border-gray-600 text-gray-200 hover:bg-gray-700 dark:border-gray-400 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
          
          {/* Lessons and Categories Tabs */}
          <Tabs defaultValue="lessons">
            <TabsList>
              <TabsTrigger value="lessons" className="tab">Lessons</TabsTrigger>
              <TabsTrigger value="categories" className="tab">Categories</TabsTrigger>
            </TabsList>
            
            {/* Lessons Tab */}
            <TabsContent value="lessons" className="mt-4">
              {isLoading ? (
                <div className="text-center py-4">Loading lessons...</div>
              ) : lessons.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No lessons found. Add your first lesson!
                </div>
              ) : (
                <div className="space-y-4">
                  {lessons.map(lesson => (
                    <Card key={lesson.id} className="overflow-hidden">
                      <CardContent className="p-4 bg-white dark:bg-gray-700 card-content">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{lesson.title}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{lesson.description}</p>
                            <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mr-2">
                                {categories.find(c => c.id === lesson.category)?.name || 'Unknown Category'}
                              </span>
                              <span>{lesson.estimatedTime} min</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(lesson)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openDeleteDialog(lesson)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Categories Tab */}
            <TabsContent value="categories" className="mt-4">
              {isLoading ? (
                <div className="text-center py-4">Loading categories...</div>
              ) : categories.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No categories found. Add your first category!
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 mt-2">
                    Use the up and down arrows to reorder categories. The order will be reflected in the frontend.
                  </p>
                  <div className="space-y-4">
                    {sortedCategories && sortedCategories.length > 0 ? sortedCategories.map((category, index) => (
                      <div key={category.id} className="space-y-4">
                        <Card className="overflow-hidden card">
                          <CardContent className="p-4 bg-white dark:bg-gray-700 flex items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{category.name}</h3>
                              {category.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {category.description}
                                </p>
                              )}
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                {lessons.filter(lesson => lesson.category === category.id).length} lessons
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1 ml-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" onClick={() => moveCategoryUp(index)}
                                            disabled={index === 0 || isLoading} className="h-8 w-8 p-0">
                                      <ChevronUp className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Move Up</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" onClick={() => moveCategoryDown(index)}
                                            disabled={index === categories.length - 1 || isLoading} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Move Down</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => openDeleteCategoryDialog(category)}
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950">
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete Category</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )) : (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                        No categories found. Categories should be initialized automatically.
                        Try refreshing the page or adding a new category.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          {/* Add Lesson Dialog */}
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogContent className="w-[90vw] sm:max-w-[800px] max-h-[85vh] overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
              <DialogHeader>
                <DialogTitle>Add New Lesson</DialogTitle>
                <DialogDescription>
                  Create a new lesson for the AI DevQuest app.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Lesson Title */}
                  <div className="col-span-2">
                    <label htmlFor="title" className="block text-sm font-medium mb-1">
                      Lesson Title <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="title"
                      value={lessonTitle}
                      onChange={(e) => setLessonTitle(e.target.value)}
                      placeholder="Enter lesson title"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Lesson Description */}
                  <div className="col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <Textarea
                      id="description"
                      value={lessonDescription}
                      onChange={(e) => setLessonDescription(e.target.value)}
                      placeholder="Enter lesson description"
                      rows={3}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={lessonCategory}
                      onValueChange={setLessonCategory}
                    >
                      <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(category => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Estimated Time */}
                  <div>
                    <label htmlFor="estimatedTime" className="block text-sm font-medium mb-1">
                      Estimated Time (minutes) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="estimatedTime"
                      type="number"
                      value={lessonEstimatedTime}
                      onChange={(e) => setLessonEstimatedTime(e.target.value)}
                      placeholder="Enter estimated time"
                      min="1"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Icon */}
                  <div className="col-span-2">
                    <label htmlFor="icon" className="block text-sm font-medium mb-1">
                      Icon (optional)
                    </label>
                    <Input
                      id="icon"
                      value={lessonIcon}
                      onChange={(e) => setLessonIcon(e.target.value)}
                      placeholder="Enter icon name or URL"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Formats */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Formats
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.VIDEO) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.VIDEO)}
                      >
                        Video
                      </Button>
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.DOCUMENT) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.DOCUMENT)}
                      >
                        Document
                      </Button>
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.LINK) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.LINK)}
                      >
                        External Links
                      </Button>
                    </div>
                  </div>
                  
                  {/* Video URL */}
                  {lessonFormats.includes(LessonFormat.VIDEO) && (
                    <div className="col-span-2">
                      <label htmlFor="videoUrl" className="block text-sm font-medium mb-1">
                        Vimeo Video URL
                      </label>
                      <Input
                        id="videoUrl"
                        value={lessonVideoUrl}
                        onChange={(e) => setLessonVideoUrl(e.target.value)}
                        placeholder="Enter Vimeo video URL"
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* Document URL */}
                  {lessonFormats.includes(LessonFormat.DOCUMENT) && (
                    <div className="col-span-2">
                      <label htmlFor="documentUrl" className="block text-sm font-medium mb-1">
                        Google Doc URL
                      </label>
                      <Input
                        id="documentUrl"
                        value={lessonDocumentUrl}
                        onChange={(e) => setLessonDocumentUrl(e.target.value)}
                        placeholder="Enter Google Doc URL"
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* External Links */}
                  {lessonFormats.includes(LessonFormat.LINK) && (
                    <div className="col-span-2">
                      <label htmlFor="externalLinks" className="block text-sm font-medium mb-1">
                        External Links (one per line)
                      </label>
                      <Textarea
                        id="externalLinks"
                        value={lessonExternalLinks}
                        onChange={(e) => setLessonExternalLinks(e.target.value)}
                        placeholder="Enter external links (one per line)"
                        rows={3}
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* Notes */}
                  <div className="col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium mb-1">
                      Notes (optional)
                    </label>
                    <Textarea
                      id="notes"
                      value={lessonNotes}
                      onChange={(e) => setLessonNotes(e.target.value)}
                      placeholder="Enter additional notes"
                      rows={3}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600">
                  Cancel
                </Button>
                <Button onClick={handleAddLesson} disabled={isLoading} className="bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">
                  {isLoading ? 'Adding...' : 'Add Lesson'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Edit Lesson Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="w-[90vw] sm:max-w-[800px] max-h-[85vh] overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
              <DialogHeader>
                <DialogTitle>Edit Lesson</DialogTitle>
                <DialogDescription>
                  Update the lesson details.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Lesson Title */}
                  <div className="col-span-2">
                    <label htmlFor="edit-title" className="block text-sm font-medium mb-1">
                      Lesson Title <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="edit-title"
                      value={lessonTitle}
                      onChange={(e) => setLessonTitle(e.target.value)}
                      placeholder="Enter lesson title"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Lesson Description */}
                  <div className="col-span-2">
                    <label htmlFor="edit-description" className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <Textarea
                      id="edit-description"
                      value={lessonDescription}
                      onChange={(e) => setLessonDescription(e.target.value)}
                      placeholder="Enter lesson description"
                      rows={3}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Category */}
                  <div>
                    <label htmlFor="edit-category" className="block text-sm font-medium mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={lessonCategory}
                      onValueChange={setLessonCategory}
                    >
                      <SelectTrigger id="edit-category" className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(category => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Estimated Time */}
                  <div>
                    <label htmlFor="edit-estimatedTime" className="block text-sm font-medium mb-1">
                      Estimated Time (minutes) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="edit-estimatedTime"
                      type="number"
                      value={lessonEstimatedTime}
                      onChange={(e) => setLessonEstimatedTime(e.target.value)}
                      placeholder="Enter estimated time"
                      min="1"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Icon */}
                  <div className="col-span-2">
                    <label htmlFor="edit-icon" className="block text-sm font-medium mb-1">
                      Icon (optional)
                    </label>
                    <Input
                      id="edit-icon"
                      value={lessonIcon}
                      onChange={(e) => setLessonIcon(e.target.value)}
                      placeholder="Enter icon name or URL"
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  {/* Formats */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Formats
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.VIDEO) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.VIDEO)}
                      >
                        Video
                      </Button>
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.DOCUMENT) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.DOCUMENT)}
                      >
                        Document
                      </Button>
                      <Button
                        type="button"
                        variant={lessonFormats.includes(LessonFormat.LINK) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleFormat(LessonFormat.LINK)}
                      >
                        External Links
                      </Button>
                    </div>
                  </div>
                  
                  {/* Video URL */}
                  {lessonFormats.includes(LessonFormat.VIDEO) && (
                    <div className="col-span-2">
                      <label htmlFor="edit-videoUrl" className="block text-sm font-medium mb-1">
                        Vimeo Video URL
                      </label>
                      <Input
                        id="edit-videoUrl"
                        value={lessonVideoUrl}
                        onChange={(e) => setLessonVideoUrl(e.target.value)}
                        placeholder="Enter Vimeo video URL"
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* Document URL */}
                  {lessonFormats.includes(LessonFormat.DOCUMENT) && (
                    <div className="col-span-2">
                      <label htmlFor="edit-documentUrl" className="block text-sm font-medium mb-1">
                        Google Doc URL
                      </label>
                      <Input
                        id="edit-documentUrl"
                        value={lessonDocumentUrl}
                        onChange={(e) => setLessonDocumentUrl(e.target.value)}
                        placeholder="Enter Google Doc URL"
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* External Links */}
                  {lessonFormats.includes(LessonFormat.LINK) && (
                    <div className="col-span-2">
                      <label htmlFor="edit-externalLinks" className="block text-sm font-medium mb-1">
                        External Links (one per line)
                      </label>
                      <Textarea
                        id="edit-externalLinks"
                        value={lessonExternalLinks}
                        onChange={(e) => setLessonExternalLinks(e.target.value)}
                        placeholder="Enter external links (one per line)"
                        rows={3}
                        className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}
                  
                  {/* Notes */}
                  <div className="col-span-2">
                    <label htmlFor="edit-notes" className="block text-sm font-medium mb-1">
                      Notes (optional)
                    </label>
                    <Textarea
                      id="edit-notes"
                      value={lessonNotes}
                      onChange={(e) => setLessonNotes(e.target.value)}
                      placeholder="Enter additional notes"
                      rows={3}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600">
                  Cancel
                </Button>
                <Button onClick={handleEditLesson} disabled={isLoading} className="bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">
                  {isLoading ? 'Updating...' : 'Update Lesson'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Delete Lesson Dialog */}
          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent className="dark:bg-gray-800 dark:border-gray-700">
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the lesson
                  "{selectedLesson?.title}".
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteLesson} className="bg-red-500 hover:bg-red-700">
                  {isLoading ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          {/* Add Category Dialog */}
          <AlertDialog open={isDeleteCategoryDialogOpen} onOpenChange={setIsDeleteCategoryDialogOpen}>
            <AlertDialogContent className="dark:bg-gray-800 dark:border-gray-700">
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the category
                  "{selectedCategory?.name}" and all lessons within it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-500 hover:bg-red-700">
                  {isLoading ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <Dialog open={isAddCategoryDialogOpen} onOpenChange={setIsAddCategoryDialogOpen}>
            <DialogContent className="w-[90vw] sm:max-w-[800px] max-h-[85vh] overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
              <DialogHeader>
                <DialogTitle>Add New Category</DialogTitle>
                <DialogDescription>
                  Create a new category for AI DevQuest lessons.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                {/* Category Name */}
                <div>
                  <label htmlFor="category-name" className="block text-sm font-medium mb-1">
                    Category Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="category-name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                {/* Category Description */}
                <div>
                  <label htmlFor="category-description" className="block text-sm font-medium mb-1">
                    Description (optional)
                  </label>
                  <Textarea
                    id="category-description"
                    value={categoryDescription}
                    onChange={(e) => setCategoryDescription(e.target.value)}
                    placeholder="Enter category description"
                    rows={3}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddCategoryDialogOpen(false)} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600">
                  Cancel
                </Button>
                <Button onClick={handleAddCategory} disabled={isLoading} className="bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">
                  {isLoading ? 'Adding...' : 'Add Category'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default AIDevQuestLessonManagementSection;